import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Collapse,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';

import DashboardIcon from '@mui/icons-material/Dashboard';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import UploadIcon from '@mui/icons-material/Upload';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import ForumIcon from '@mui/icons-material/Forum';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// Import necessary dependencies for profile image
import peacemakerImg from '../../../assets/images/homeimages/peacemakerImg.png';
import { apiServices } from '../../../services/apiServices';

const drawerWidth = 300;

const DashboardSidebar = ({
  sidebarOpen,
  mobileOpen,
  handleDrawerToggle,
  onMenuSelect,
  selectedMenu
}) => {
  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [open, setOpen] = useState(false);
  const [avatar, setAvatar] = useState(peacemakerImg);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Add profile image state management
  const [profileImage, setProfileImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Get user data from Redux store
  const { user, isLoading } = useSelector((state) => state.auth);
  console.log('User data:', user);
  const userName = user?.username || user?.name || 'Loading...';
  const peacekeeperId = user?.peacekeeper_id || (isLoading ? 'Loading...' : 'IN-0000000-PK');
  const country = user?.country || "";

  // Fetch profile image when component mounts
  const fetchProfileImage = async (forceRefresh = false) => {
    if (user?.userid) {
      setImageLoading(true);
      try {
        // Clear existing profile image URL to prevent Safari caching issues
        if (profileImage && profileImage.startsWith('blob:')) {
          URL.revokeObjectURL(profileImage);
          setProfileImage(null);
          setAvatar(null);
        }

        // Use getBlobAPI for proper blob handling
        // Try getBlobAPI first
        let response;
        try {
          // Add cache busting for Safari - append timestamp to URL
          const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
          const apiUrl = `profile-image/${user.userid}${cacheBuster}`;
          
          response = await apiServices.getBlobAPI(apiUrl);
          console.log('Profile image API response (getBlobAPI):', response);
        } catch (blobError) {
          console.log('getBlobAPI failed, trying regular getAPI:', blobError);
          // Fallback to regular getAPI if getBlobAPI fails
          const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
          const apiUrl = `profile-image/${user.userid}${cacheBuster}`;
          
          response = await apiServices.getAPI(apiUrl);
          console.log('Profile image API response (getAPI):', response);
        }
        
        console.log('Response status:', response.status);
        console.log('Response status text:', response.statusText);
        console.log('Response data:', response.data);
        console.log('Response data type:', typeof response.data);
        console.log('Response data length:', response.data?.length);
        console.log('Response data preview:', typeof response.data === 'string' ? response.data.substring(0, 100) : 'Not a string');
        console.log('Response headers:', response.headers);
        
        if (response.data) {
          // response.data is already a Blob when using getBlobAPI
          console.log('Response data type:', typeof response.data);
          console.log('Response data constructor:', response.data.constructor.name);
          console.log('Response data size:', response.data.size);
          console.log('Response headers:', response.headers);
          
          try {
            let blobData = response.data;
            
            // If the data is a string, we need to handle it differently
            if (typeof response.data === 'string') {
              console.warn('Data received as string, attempting to convert to blob');
              
              // Check if it's a base64 encoded string
              if (response.data.startsWith('data:image/')) {
                // It's already a data URL, use it directly
                const imageUrl = forceRefresh ? `${response.data}?t=${Date.now()}` : response.data;
                setProfileImage(imageUrl);
                setAvatar(imageUrl);
                return;
              }
              
              // If it's a string that starts with PNG (like in your case), try to convert it
              if (response.data.startsWith('PNG')) {
                console.log('Detected PNG string data, converting to blob');
                // Convert the string to a Uint8Array by parsing the hex values
                const hexString = response.data.replace(/\\x([0-9A-Fa-f]{2})/g, (match, hex) => {
                  return String.fromCharCode(parseInt(hex, 16));
                });
                
                // Create a blob from the processed string
                blobData = new Blob([hexString], { 
                  type: response.headers['content-type'] || 'image/png' 
                });
              } else {
                // Try to convert as regular text
                const encoder = new TextEncoder();
                const uint8Array = encoder.encode(response.data);
                blobData = new Blob([uint8Array], { 
                  type: response.headers['content-type'] || 'image/png' 
                });
              }
            }
            
            const imageUrl = URL.createObjectURL(blobData);
            console.log('Created blob URL:', imageUrl);
            console.log('Blob data size:', blobData.size);
            console.log('Blob data type:', blobData.type);
            setProfileImage(imageUrl);
            setAvatar(imageUrl);
            console.log('Profile image state set to:', imageUrl);
          } catch (blobError) {
            console.error('Error creating blob URL:', blobError);
            // Fallback to default image if blob creation fails
            setProfileImage(peacemakerImg);
            setAvatar(peacemakerImg);
          }
        }
      } catch (error) {
        console.error('Error fetching profile image:', error);
        // Fallback to default image if API fails
        setProfileImage(peacemakerImg);
        setAvatar(peacemakerImg);
      } finally {
        setImageLoading(false);
      }
    }
  };

  useEffect(() => {
    // Use cache busting on initial load to prevent Safari from showing cached images
    fetchProfileImage(true);
  }, [user?.userid]);

  // Listen for profile update events from EditProfile.js
  useEffect(() => {
    const handleProfileImageUpdate = async (event) => {
      console.log('Profile image update event received in DashboardSidebar:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing profile image in DashboardSidebar after profile image update...');
        
        // Wait a moment for the backend to process, then refresh with cache busting for Safari
        setTimeout(async () => {
          await fetchProfileImage(true);
        }, 1000); // Wait 1 second for backend processing
      }
    };

    const handleProfileInfoUpdate = async (event) => {
      console.log('Profile info update event received in DashboardSidebar:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing profile image in DashboardSidebar after profile info update...');
        
        // Wait a moment for the backend to process, then refresh with cache busting for Safari
        setTimeout(async () => {
          await fetchProfileImage(true);
        }, 1000); // Wait 1 second for backend processing
      }
    };

    // Add event listeners
    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    window.addEventListener('profileInfoUpdated', handleProfileInfoUpdate);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
      window.removeEventListener('profileInfoUpdated', handleProfileInfoUpdate);
    };
  }, [user?.userid]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (profileImage && profileImage.startsWith('blob:')) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    // Clear preview image when dialog is closed
    setPreviewImage(null);
  };
  
  const handleSave = () => {
    if (previewImage) {
      // If there's a preview image, upload it
      handleUploadProfileImage();
    } else {
      // No changes to save, just close the dialog
      handleClose();
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl); // For dialog preview
      setAvatar(imageUrl);       // Also update actual avatar
    }
  };

  const handleUploadProfileImage = async () => {
    if (!previewImage || !user?.userid) {
      console.error('No image selected or user ID missing');
      return;
    }

    setUploading(true);
    try {
      // Create FormData for the upload
      const formData = new FormData();
      
      // Convert the preview image URL back to a file
      const response = await fetch(previewImage);
      const blob = await response.blob();
      const file = new File([blob], 'profile-image.jpg', { type: blob.type });
      
      formData.append('profileImage', file);
      formData.append('userId', user.userid);
      formData.append('imageType', 'profile');

      // Upload the image
      const uploadResponse = await apiServices.postMultiPartFormDataAPI('upload-profile-image', formData);

      if (uploadResponse.status === 200 || uploadResponse.status === 201) {
        console.log('Profile image uploaded successfully');
        
        // Dispatch a custom event to notify other components that profile image has been updated
        const profileImageUpdateEvent = new CustomEvent('profileImageUpdated', {
          detail: {
            userId: user.userid,
            timestamp: new Date().toISOString(),
            message: 'Profile image has been updated successfully from sidebar'
          }
        });
        window.dispatchEvent(profileImageUpdateEvent);
        
        // Small delay for Safari to process the image update
        await new Promise(resolve => setTimeout(resolve, 500));

        // Refresh the profile image from the API with cache busting for Safari
        await fetchProfileImage(true);
        
        // Clear the preview image
        setPreviewImage(null);
        
        // Show success message
        setShowSuccessMessage(true);
        
        // Close the dialog after a short delay
        setTimeout(() => {
          handleClose();
          setShowSuccessMessage(false);
        }, 1500);
      } else {
        console.error('Failed to upload profile image:', uploadResponse.statusText || uploadResponse.status);
        alert('Failed to upload profile image. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      // Check if it's an API error with response data
      if (error.response?.data) {
        alert(`Error uploading profile image: ${error.response.data.message || error.response.data}`);
      } else {
        alert('Error uploading profile image. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };


  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Profile Section */}
      <Box
        sx={{
          minHeight: 185,
          position: 'relative',
          backgroundColor: '#fff',
          borderRadius: 2,
          m: 2,
          boxShadow: 1,
          overflow: 'hidden',
          pb: 2,
          p: 1,
        }}
      >
        {/* Background image */}
        <Box
          sx={{
            height: 80,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
          }}
          className="dashboardSidebarBg"
        />

        {/* Avatar and Edit */}
        <Box>
          <Avatar
            alt={userName}
            src={profileImage || avatar}
            sx={{
              width: 50,
              height: 50,
              position: 'absolute',
              top: 60,
              left: '14%',
              transform: 'translateX(-50%)',
              border: '2px solid #fff',
              bgcolor: user?.name ? '#214F7D' : '#ccc',
            }}
          >
            {imageLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : !profileImage && !avatar ? (
              <Typography variant="h6" color="text.secondary">
                {userName.charAt(0).toUpperCase() || 'U'}
              </Typography>
            ) : null}
          </Avatar>

          {/* <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              position: 'absolute',
              top: 88,
              left: '20%',
              transform: 'translateX(-50%)',
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              zIndex: 2,
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <EditIcon fontSize="small" className='editIcon' />
          </IconButton> */}

          {/* Edit Dialog */}
          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Profile Information</DialogTitle>
            <DialogContent>
              {/* Information Notice */}
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Note:</strong> Profile information is read-only and managed by your account settings. 
                  You can only update your profile picture here.
                </Typography>
              </Box>

              {/* User ID */}
              {user?.id && (
                <TextField
                  label="User ID"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.id}
                  disabled
                  helperText="Unique user identifier"
                />
              )}

              {/* Username */}
              {user?.username && (
                <TextField
                  label="Username"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.username}
                  disabled
                  helperText="Username is managed by your account settings"
                />
              )}

              {/* Full Name */}
              {user?.name && (
                <TextField
                  label="Full Name"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.name}
                  disabled
                  helperText="Full name is managed by your account settings"
                />
              )}

              {/* Email */}
              {user?.email && (
                <TextField
                  label="Email Address"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.email}
                  disabled
                  helperText="Email is managed by your account settings"
                />
              )}

              {/* Peacekeeper ID */}
              {user?.peacekeeper_id && (
                <TextField
                  label="Peacekeeper ID"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.peacekeeper_id}
                  disabled
                  helperText="Your unique peacekeeper identifier"
                />
              )}

              {/* Phone Number */}
              {user?.phone && (
                <TextField
                  label="Phone Number"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.phone}
                  disabled
                  helperText="Phone number is managed by your account settings"
                />
              )}

              {/* Country */}
              {user?.country && (
                <TextField
                  label="Country"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.country}
                  disabled
                  helperText="Country is managed by your account settings"
                />
              )}

              {/* Has Password Status */}
              {user?.hasPassword !== undefined && (
                <TextField
                  label="Password Status"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={user.hasPassword ? "Password Set" : "No Password Set"}
                  disabled
                  helperText="Password status for your account"
                />
              )}

              {/* No Data Available Message */}
              {(!user || Object.keys(user).length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No profile information available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Please log in again to refresh your profile data
                  </Typography>
                </Box>
              )}

              {/* Profile Picture Section */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'text.primary' }}>
                Profile Picture
              </Typography>
              
              <Box
                sx={{
                  border: '2px dashed #cbd5e1',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  bgcolor: uploading ? '#f5f5f5' : '#f9fafb',
                  opacity: uploading ? 0.7 : 1,
                }}
                onClick={() => !uploading && document.getElementById('upload-input').click()}
              >
                {uploading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: 4
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      border: '4px solid #f3f3f3',
                      borderTop: '4px solid #214F7D',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      mb: 2
                    }} />
                    <Typography variant="body2" color="text.secondary">
                      Uploading image...
                    </Typography>
                  </Box>
                ) : previewImage ? (
                  <Box
                    component="img"
                    src={previewImage}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                ) : profileImage ? (
                  <Box
                    component="img"
                    src={profileImage}
                    alt="Current Profile"
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: 64, color: '#90a4ae', mb: 1 }} />
                    <Typography variant="body1">
                      Drop your image here, or{' '}
                      <Typography component="span" color="primary" fontWeight="bold">
                        browse
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supports: JPG, JPEG, PNG (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  type="file"
                  accept="image/*"
                  id="upload-input"
                  hidden
                  onChange={handleImageChange}
                  disabled={uploading}
                />
              </Box>

              {/* Success Message */}
              {showSuccessMessage && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: '#d4edda', 
                  borderRadius: 1, 
                  border: '1px solid #c3e6cb',
                  color: '#155724'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    ✓ Profile image uploaded successfully!
                  </Typography>
                </Box>
              )}

            </DialogContent>
            <DialogActions sx={{mb:2 , px:3}}>
              <Button 
                onClick={handleClose} 
                className='cancelButton'
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSave}
                disabled={uploading || !previewImage}
                sx={{
                  background: 'linear-gradient(to right, #214F7D, #3A8139)',
                  color: 'white',
                  borderRadius:"50px"
                }}
              >
                {uploading ? 'Uploading...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Name and ID */}
        <Box sx={{ mt: 1, textAlign: 'left', px: 2, pl: 8 }}>
          <Typography variant="subtitle1" fontWeight={100} className='profileName'>
            {userName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
              backgroundColor: '#00000024',
              padding: '4px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              fontSize: 11
            }}>
            {country} {peacekeeperId}
          </Typography>
          {imageLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontStyle: 'italic' }}>
              Loading profile image...
            </Typography>
          )}
        </Box>
      </Box>

      {/* Menu List */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        <SidebarItem
          text="Invited Users"
          icon={<DashboardIcon />}
          active={selectedMenu === 'Invited Users'}
          onClick={() => onMenuSelect('Invited Users')}
        />
        <SidebarItem
          text="Edit Profile"
          icon={<EditIcon />}
          active={selectedMenu === 'Edit Profile'}
          onClick={() => onMenuSelect('Edit Profile')}
        />

        <SidebarItem
          text="Certificate"
          icon={<WorkspacePremiumIcon />}
          active={selectedMenu === 'Certificate'}
          onClick={() => onMenuSelect('Certificate')}
        />
        <SidebarItem
          text="Visiting Card"
          icon={<CreditCardIcon />}
          active={selectedMenu === 'Visiting Card'}
          onClick={() => onMenuSelect('Visiting Card')}
        />

        <SidebarItem
          text="Send Invitation"
          icon={<SendIcon />}
          active={selectedMenu === 'Send Invitation'}
          onClick={() => onMenuSelect('Send Invitation')}
        />
        <SidebarItem
          text="Group Chat"
          icon={<ForumIcon />}
          active={selectedMenu === 'Group Chat'}
          onClick={() => onMenuSelect('Group Chat')}
        />
        {/* <SidebarItem
          text="Chat"
          icon={<ChatIcon />}
          active={selectedMenu === 'Chat'}
          onClick={() => onMenuSelect('Chat')}
        /> */}
        <SidebarItem
          text="Referrals"
          icon={<PeopleIcon />}
          active={selectedMenu === 'Referrals'}
          onClick={() => onMenuSelect('Referrals')}
        />
        {/* {!window.cordova &&
        <SidebarItem
          text="User Management"
          icon={<ManageAccountsIcon />}
          active={selectedMenu === 'User Management'}
          onClick={() => onMenuSelect('User Management')}
        />
        } */}
        <SidebarItem
          text="Change Password"
          icon={<LockIcon />}
          active={selectedMenu === 'Change Password'}
          onClick={() => onMenuSelect('Change Password')}
        />
      </List>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', p: 2, borderTop: '1px solid #eee', fontSize: 12 }}>
        VERSION: 1.0.4
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: sidebarOpen ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}
      aria-label="sidebar"
      className="sideBarSection"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        className='mobileSidebarDrawer'
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? drawerWidth : 0,
            transition: 'width 0.3s',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

const SidebarItem = ({ text, icon, active, onClick, nested = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleItemClick = () => {
    onClick();
    // Only close drawer when a menu item is clicked, not on accordion toggles
    // The drawer closing is now handled in the parent component (IAMDashboard)
  };

  return (
    <ListItem disablePadding sx={{ pl: nested ? 4 : 2 }}>
      <ListItemButton
        onClick={handleItemClick}
        selected={active}
        sx={{
          mx: nested ? 0 : 1,
          borderRadius: '50px',
          backgroundColor: active ? '#FED304' : 'transparent',
          '&.Mui-selected': {
            backgroundColor: '#FED304',
            fontWeight: 'bold',
            borderRadius: '50px',
            color:'#0c5c88'
          },
        }}
      >
        <ListItemIcon sx={{ color: '#333', minWidth: 36 }}>{icon}</ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );
};

export default DashboardSidebar;
